"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Clock, Loader, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const benefits = [{
  id: "1",
  title: "Direct Access to Yassine",
  desc: "You work directly with me—an experienced SEO strategy consultant. No junior staff or account managers."
}, {
  id: "2",
  title: "Tailored SEO Recommendations",
  desc: "Every suggestion is customized specifically for your website, market competition, and organic growth goals."
}, {
  id: "3",
  title: "Honest, No-Pressure Guidance",
  desc: "A genuine evaluation of your search traffic potential with zero aggressive sales pitches or lock-in contracts."
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-muted/60 border border-border rounded-xl text-center space-y-4">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-accent/20 text-accent">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold font-default text-foreground">
          Consultation Request Received
        </h3>
        <p className="text-muted-foreground text-sm font-default leading-relaxed">
          {successMessage || "Thank you! I will personally review your website details and email you within 24 hours to schedule our strategy call."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Work email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@yourcompany.com" className="bg-background text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">Website Address</FormLabel>
            <FormControl>
              <Input placeholder="www.yourcompany.com" className="bg-background text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-11 text-base font-medium shadow-md transition-[color,background-color,box-shadow]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending Request...
          </> : <>
            Book My Free SEO Consultation
            <ArrowRight className="ml-1 size-4" />
          </>}
      </Button>

      <p className="text-xs text-center text-muted-foreground font-default pt-1">
        100% free with zero obligation. I strictly respect your business privacy.
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 space-y-6">
            <Badge variant="secondary" className="px-3 py-1 text-sm bg-secondary text-secondary-foreground font-medium rounded-full">
              <SafeHydrate><Calendar className="size-3.5 mr-1" /></SafeHydrate>
              Direct Consultant Access
            </Badge>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground leading-tight">
              Ready to Scale Your Organic Traffic with Sustainable SEO?
            </h2>

            <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
              Book a free 30-minute consultation directly with me, Yassine Essaadi. I will analyze your current search visibility, highlight technical roadblocks, and outline a realistic strategy for long-term growth.
            </p>

            <div className="space-y-3.5 pt-2">
              {benefits.map((item, i) => <div data-index={i} key={item.id} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card border border-border shadow-xs transition-[border-color,box-shadow]">
                  <div className="mt-0.5 shrink-0 size-7 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground font-default text-base">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-default mt-0.5">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-t border-border font-default">
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-primary" />
                <span>24-hour response time</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>Zero sales pressure</span>
              </div>
              <Link to="/ContactPage" className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
                View all contact info
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="shadow-xl border-border bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold font-serif text-card-foreground">
                  Book Your Free Consultation
                </CardTitle>
                <CardDescription className="text-muted-foreground font-default text-sm">
                  Provide your website details below to schedule a 1-on-1 strategy session directly with Yassine.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                name: "",
                email: "",
                website: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book My Free SEO Consultation" successMessage="Thank you! I will personally review your website details and email you within 24 hours.">
                  <ConsultationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}